import type { ComponentPropsWithRef, ReactNode } from 'react';

/**
 * The envelope a `disabled` control needs to carry a tooltip.
 *
 * A `disabled` control receives neither pointer nor focus, so the package `Tooltip` (Radix) can
 * never open on it: the bubble that says WHY it is closed hangs on this span instead, which takes
 * the focus the control lost (`tabIndex` 0 only while closed) and draws the focus ring the
 * control would have drawn. `...rest` is load-bearing: the tooltip's trigger is this span, and it
 * hands it its handlers and its ref.
 *
 * Not a UI component of its own: it draws nothing but the focus ring of the charter. The package
 * has no equivalent yet (proposed: a `disabledReason` on `Button`).
 */
export function TooltipShield({
  closed,
  layout,
  children,
  ...rest
}: Omit<ComponentPropsWithRef<'span'>, 'className' | 'children' | 'tabIndex'> & {
  /** True when the control inside is `disabled`. */
  readonly closed: boolean;
  /** What the envelope takes over from the element it stands in for in the layout. */
  readonly layout?: string;
  readonly children: ReactNode;
}) {
  return (
    <span
      {...rest}
      tabIndex={closed ? 0 : undefined}
      className={`inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${layout ?? ''}`}
    >
      {children}
    </span>
  );
}
